/*
 * Ryan O'Dowd
 * 2024-03-01
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import {
  apiUpdateClassroomGoal,
  apiUpdateGoal,
  apiUpdateGoalTags,
} from '@utils/store/actions';
import Autocomplete from 'react-native-autocomplete-input';
import Banner from '@components/common/Banner';
import Button from '@components/common/Button';
import Checkbox from '@components/common/Checkbox';
import DateRange from '../DateRange';
import Dialog from '@components/common/Dialog';
import FormControl from '@components/common/FormControl';
import PublishButton from '../PublishButton';
import Select from '@components/common/Select';
import Switch from '@components/common/Switch';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';

const DRAFT_TEXT = `This is a draft and won't be available to students until it is published.  If you are ready to publish it, click the "Publish" button.`; // @TODO: combine with ClassroomGoal
const PUBLISHED_TEXT = (datePublished) => `Published on ${dateFnsFormat(new Date(datePublished), 'MMMM d, yyyy')} and currently available to students.`; // @TODO: combine with ClassroomGoal

const EditGoalDialog = (props: EditGoalDialogProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const dispatch = hooks.useTypedDispatch();

  const [_goalName, setGoalName] = useState(props.goal.title || '');
  const [_goalType, setGoalType] = useState(props.goal.goal_type);
  const [_goalDescription, setGoalDescription] = useState(props.goal.description || '');
  const [_isPublic, setIsPublic] = useState(props.goal.classroom_goal_metadata.is_public !== undefined ? !!props.goal.classroom_goal_metadata.is_public : !!props.isAdmin);
  const [_dateAvailable, setDateAvailable] = useState(props.goal.date_available ? new Date(props.goal.date_available) : null);
  const [_dateDeadline, setDateDeadline] = useState(props.goal.date_deadline ? new Date(props.goal.date_deadline) : null);
  // @TODO: const [_status, setStatus] = useState(props.goal.classroom_goal_metadata.status);
  const [_canChooseTranslation, setCanChooseTranslation] = useState(props.goal.classroom_goal_metadata.can_choose_translation); // @TODO: default to true for public goals and false for classroom goals

  const tags = hooks.useTypedSelector((state) => state.tags);
  const [_tags, setTags] = useState(props.goal.classroom_goal_metadata.tags.map((t) => tags[t.tag_id].name));

  return (
    <Dialog
      visible={true}
      onDismiss={() => props.onClose()} // @TODO: ask about unsaved changes
    >
      <Dialog.Title>Edit goal</Dialog.Title>{/* @TODO: i18n */}
      <Dialog.Content>
        <span style={styles.publishedStatusWrapper}>
          <Banner
            type={props.goal.classroom_goal_metadata.status === 'draft' ? 'warning' : 'success'}
            leftButton={props.goal.classroom_goal_metadata.status === 'draft' && <PublishButton classroomId={props.goal.classroom_goal_metadata.classroom_id} goalId={props.goal.id} />}
            text={props.goal.classroom_goal_metadata.status === 'draft' ? DRAFT_TEXT : PUBLISHED_TEXT(props.goal.classroom_goal_metadata.date_published)}// @TODO: text isn't showing up on localhost here for drafts // @TODO: this is because the globals file is missing?
          />
        </span>

        <TextInput
          value={_goalName}
          mode='outlined'
          onChangeText={(text) => setGoalName(text)}
          label='Goal name'
        />

        {/* @TODO:
        <TextInput
          value={_goalDescription}
          mode='outlined'
          onChangeText={(text) => setGoalDescription(text)}
          multiline={true}
          numberOfLines={4}
          label='Description'
        />
        */}

        {props.isAdmin && // @TODO: not for v1 for classrooms...only for public goals right now
          <FormControl
            addPadding={true}
            label='Translation'
          >
            <Checkbox
              isChecked={!!_canChooseTranslation}
              onPress={() => setCanChooseTranslation((curr) => !curr)}
              label='Allow students to choose their preferred Bible translation.'
            />
          </FormControl>
        }

        <DateRange
          dateAvailable={_dateAvailable}
          dateDeadline={_dateDeadline}
          setDateAvailable={(newValue) => setDateAvailable(newValue)}
          setDateDeadline={(newValue) => setDateDeadline(newValue)}
          hideClear={true}
        />
        {/* @TODO: show warning for non-null dates if this is a public goal (admin only) */}

        {props.isAdmin &&
          <FormControl
            addPadding={true}
            label='Admin options'
            style={styles.adminSettingsWrapper}
          >
            <View style={styles.adminOptionsInner}>
              <Switch
                isChecked={!!_isPublic}
                onValueChange={() => setIsPublic((curr) => !curr)}
                label='Public goal?'
              />

              <br />{/* @TODO: shouldn't need this */}

              <Select
                label='Goal type'
                options={[
                  {
                    label: 'Memory',
                    value: 'memory',
                  },
                  {
                    label: 'Group',
                    value: 'group',
                  },
                  {
                    label: 'Reading',
                    value: 'reading',
                  },
                ]}
                value={_goalType}
                onChange={(value) => setGoalType(value)}
              />

              <br />{/* @TODO: shouldn't need this */}

              <Autocomplete
                containerStyle={styles.bookContainer}
                // @TODO: inputContainerStyle={[styles.autocompleteContainerInner, _showCorrectAnswer && CPassage.getBookName(props.passage.translation_id, props.passage.book_id) !== _bookNameText && styles.incorrect]}
                // @TODO: listContainerStyle={styles.autocompleteListContainerStyle}
                // @TODO: style={[styles.customTextInput, _showCorrectAnswer && CPassage.getBookName(props.passage.translation_id, props.passage.book_id) !== _bookNameText && styles.incorrect]}
                // @TODO: hideResults={!_bookNameText || _bookNameText.length < 2 || _isValidBook(_bookNameText)}
                data={Object.values(tags).map((tag: any) => tag.name)}
                value={_tags}
                // @TODO: onBlur={() => setBookNameText(_bookNameText?.trim() || '')}
                onChangeText={(t) => setTags(t)}// @TODO: allow multiple to be selected
                // @TODO: make sure adding new tags works
                placeholder='Tags (TODO: this is not currently supported)'
                flatListProps={{// @TODO: options are not scrollable
                  keyExtractor: (b: any) => b.id,
                  renderItem: ({item}: any) => {
                    return (
                      <TouchableOpacity
                        key={item.id}
                        // @TODO: onPress={() => setBookNameText(item.name)}
                      >
                        <Text style={[styles.customTextInput, styles.autocompleteOptionText]}>{item.name}</Text>
                      </TouchableOpacity>
                    );
                  },
                  keyboardShouldPersistTaps: 'always',
                }}
              />
            </View>
          </FormControl>
        }
      </Dialog.Content>
      <Dialog.Actions>
        {/* @TODO:
        <Button
          color={styles.colors.alert}
          mode='text'
          onPress={() => {
            // @TODO: confirm with alert after letting teacher know how permanent this is...show how many kids have started (maybe not an option if goal is published)
            props.onClose();
          }}
        >
          Delete
        </Button>
        */}
        {/* @TODO: button to duplicate goal */}
        <Button
          mode='text'
          onPress={() => props.onClose()}
        >
          {i18n('cancel')}
        </Button>
        <Button
          onPress={() => {
            dispatch(apiUpdateGoal(props.goal.id, _goalName, _goalDescription, _goalType, null, null, _dateAvailable, _dateDeadline, undefined, undefined, () => { // @TODO: teachers should use different endpoint than this
              // @TODO: don't like that this is a callback, but it prevents race condition when updating name/description:
              /* @TODO: when editing a goal, there are 3 api calls (patches for goal, goal metadata, and tags), and there's a race condition in updating name/description because `goal` is returned not only in the goal patch, but in the other two as well (but it hasn't been updated yet, so it returns the old goal name) */
              if (JSON.stringify(_tags.map((t) => t.id)) !== JSON.stringify(props.goal.classroom_goal_metadata.tags.map((t) => t.id))) {
                dispatch(apiUpdateGoalTags(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, _tags));
              }
              const classroomGoalUpdates: any = {};
              if (_canChooseTranslation !== props.goal.classroom_goal_metadata.can_choose_translation) {
                classroomGoalUpdates.canChooseTranslation = _canChooseTranslation;
              }
              if (_isPublic !== props.goal.classroom_goal_metadata.is_public) {
                classroomGoalUpdates.isPublic = _isPublic;
              }
              if (Object.keys(classroomGoalUpdates).length) {
                dispatch(apiUpdateClassroomGoal(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, classroomGoalUpdates));
              }
              props.onClose(); // @TODO: wait for back-end call to complete?
            })); // @TODO: this should be only fields that have been updated (compare state variables with props.goal)
          }}
        >
          {i18n('save')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

class EditGoalDialogProps {
  onClose: () => void;
  goal: any;

  isAdmin?: boolean;
};

export default EditGoalDialog;
